import { useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { Box, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import FullScreenLoader from '../../components/Shared/FullScreenLoader'
import { useAppSelector } from '../../redux/store'
import {
  GET_TENANT_PARKING_POOL_CONFIG,
  IGetTenantParkingPoolConfigData,
  IGetTenantParkingPoolConfigVars,
} from '../../graphql/tenant/getTenantParkingPoolConfig'
import {
  IStatParkingPoolAvailabilityData,
  IStatParkingPoolAvailabilityVars,
  STAT_PARKING_POOL_AVAILABILITY,
} from '../../graphql/parkingPool/statParkingPoolAvailability'
import {
  IStatParkingPoolEmployeeUsageData,
  IStatParkingPoolEmployeeUsageVars,
  STAT_PARKING_POOL_EMPLOYEE_USAGE,
} from '../../graphql/parkingPool/statParkingPoolEmployeeUsage'
import {
  periodTotals,
  previousRangeForDate,
  previousRangeForDays,
  rangeForDate,
  rangeForDays,
  IBuildingTotals,
  totalsByBuilding,
  totalsByDay,
  totalsByPoolParking,
  utilizationPct,
} from '../../utils/parkingPoolStats'
import { chartSeries, chartSeriesOther, colors } from '../../const/colors'
import EmptyState from '../../components/Shared/EmptyState'
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined'
import { ProfileLimitGrid } from '../../components/ParkingUserDetails/ProfileLegend'
import UsageOverTimeChart from '../../components/Parking/UsageOverTimeChart'
import EmployeeUsageSection from '../../components/Parking/EmployeeUsageSection'
import DayPickerPill from '../../components/Parking/DayPickerPill'
import BuildingsTable, {
  IBuildingRow,
} from '../../components/Parking/BuildingsTable'
import StatValue from '../../components/Shared/StatValue'
import { dateIso2localeString } from '../../utils/formatDate'

// TO DO!!! zdjąć flagę i pokazać % średniego wykorzystania oraz wykres, gdy
// potwierdzimy na realnych danych semantykę reserved vs used i dni bez slotów
const SHOW_UTILIZATION_STATS: boolean = false

const SHOW_PROFILES: boolean = false
const SHOW_EMPLOYEE_USAGE: boolean = false

const MAX_SERIES = chartSeries.length

const buildingKey = (building: IBuildingTotals) =>
  String(building.id ?? 'no-building')

const DATE_FORMAT = { year: 'numeric', month: '2-digit', day: '2-digit' } as const

const PERIOD_DAYS = { day: 1, '30': 30, '90': 90, year: 365 } as const
type Period = keyof typeof PERIOD_DAYS

const PERIODS: Period[] = ['day', '30', '90', 'year']

const PERIOD_LABEL_KEYS: Record<Period, string> = {
  day: 'period_day',
  '30': 'period_30',
  '90': 'period_90',
  year: 'period_year',
}

const ParkingPoolData = () => {
  const { t, i18n } = useTranslation(['parking_pool_data', 'generic'])
  const NONE = t('generic:none')
  const { tenantName } = useAppSelector(state => state.user)
  const [period, setPeriod] = useState<Period>('day')
  const [day, setDay] = useState(() => dayjs().format('YYYY-MM-DD'))
  const days = PERIOD_DAYS[period]
  const isSingleDay = period === 'day'

  const { data, loading } = useQuery<
    IGetTenantParkingPoolConfigData,
    IGetTenantParkingPoolConfigVars
  >(GET_TENANT_PARKING_POOL_CONFIG, {
    fetchPolicy: 'cache-and-network',
    variables: { name: tenantName },
  })

  const pool = data?.getTenant?.parkingPool
  const parkingPool = pool?.id ?? ''

  const range = useMemo(
    () => (isSingleDay ? rangeForDate(day) : rangeForDays(days)),
    [isSingleDay, day, days],
  )
  const previousRange = useMemo(
    () => (isSingleDay ? previousRangeForDate(day) : previousRangeForDays(days)),
    [isSingleDay, day, days],
  )

  const { data: statsData, loading: statsLoading } = useQuery<
    IStatParkingPoolAvailabilityData,
    IStatParkingPoolAvailabilityVars
  >(STAT_PARKING_POOL_AVAILABILITY, {
    fetchPolicy: 'cache-and-network',
    skip: !parkingPool,
    variables: { parkingPool, ...range },
  })

  const { data: previousStatsData } = useQuery<
    IStatParkingPoolAvailabilityData,
    IStatParkingPoolAvailabilityVars
  >(STAT_PARKING_POOL_AVAILABILITY, {
    fetchPolicy: 'cache-and-network',
    skip: !parkingPool || !SHOW_UTILIZATION_STATS,
    variables: { parkingPool, ...previousRange },
  })

  const { data: employeeUsageData, loading: employeeUsageLoading } = useQuery<
    IStatParkingPoolEmployeeUsageData,
    IStatParkingPoolEmployeeUsageVars
  >(STAT_PARKING_POOL_EMPLOYEE_USAGE, {
    fetchPolicy: 'cache-and-network',
    skip: !parkingPool || !SHOW_EMPLOYEE_USAGE,
    variables: { parkingPool, ...range },
  })

  const availability = statsData?.statParkingPoolAvailability
  const stats = availability?.stats ?? []
  const statsFailed = availability?.status === false
  const statsPending = statsLoading && !statsData

  const totals = periodTotals(stats)
  const utilization = utilizationPct(totals)
  const previousUtilization = utilizationPct(
    periodTotals(previousStatsData?.statParkingPoolAvailability.stats ?? []),
  )
  const deltaPp =
    utilization != null && previousUtilization != null
      ? utilization - previousUtilization
      : null

  const dayTotals = totalsByDay(stats)
  const parkingTotals = totalsByPoolParking(stats)

  const latestDay = dayTotals[dayTotals.length - 1]?.day
  const latestRows = stats.filter(
    row => dayjs(row.day).format('YYYY-MM-DD') === latestDay,
  )
  const latestTotals = periodTotals(latestRows)
  const latestByParking = totalsByPoolParking(latestRows)
  const latestIsToday = latestDay === dayjs().format('YYYY-MM-DD')

  const latestByBuilding = new Map(
    totalsByBuilding(latestRows).map(row => [buildingKey(row), row]),
  )
  const buildingRows: IBuildingRow[] = totalsByBuilding(stats).map(building => {
    const now = latestByBuilding.get(buildingKey(building))
    return {
      key: buildingKey(building),
      name: building.name ?? t('no_building'),
      parkingNames: building.parkings.map(parking => parking.parkingName),
      sharedWith: building.sharedWith,
      overCapacity: building.overCapacity,
      places: now?.capacity ?? 0,
      free: now?.free ?? 0,
      reserved: building.reserved,
      used: building.used,
      pct: utilizationPct(building),
    }
  })

  const employeeUsage = employeeUsageData?.statParkingPoolEmployeeUsage
  const employeeRows = employeeUsage?.stats ?? []
  const employeeFailed = employeeUsage?.status === false
  const employeePending = employeeUsageLoading && !employeeUsageData

  const profiles = pool?.profiles ?? []
  const poolParkings = pool?.poolParkings ?? []

  const parkingRows = parkingTotals.map(parking => {
    const now = latestByParking.find(
      row => String(row.poolParkingId) === String(parking.poolParkingId),
    )
    return {
      id: String(parking.poolParkingId),
      name: parking.parkingName,
      placesNow: now?.capacity ?? 0,
      capacity: parking.capacity,
      used: parking.used,
      pct: utilizationPct(parking),
      isOther: false,
    }
  })

  const tailRows = parkingRows.slice(MAX_SERIES - 1)
  const donutRows =
    parkingRows.length > MAX_SERIES
      ? [
          ...parkingRows.slice(0, MAX_SERIES - 1),
          {
            id: 'other',
            name: t('other_parkings', { n: tailRows.length }),
            placesNow: tailRows.reduce((sum, row) => sum + row.placesNow, 0),
            capacity: tailRows.reduce((sum, row) => sum + row.capacity, 0),
            used: tailRows.reduce((sum, row) => sum + row.used, 0),
            pct: utilizationPct({
              capacity: tailRows.reduce((sum, row) => sum + row.capacity, 0),
              used: tailRows.reduce((sum, row) => sum + row.used, 0),
              reserved: 0,
              free: 0,
            }),
            isOther: true,
          },
        ]
      : parkingRows

  const seriesColor = (index: number, isOther: boolean) =>
    isOther ? chartSeriesOther : chartSeries[index]

  // TO DO!!! ustalić jedno źródło liczby miejsc — ten kafel pokazuje sumę z umowy,
  // a donut pojemność z day slotów; przy rozjeździe użytkownik widzi dwie liczby
  const contractPlaces = poolParkings.length
    ? poolParkings.reduce((sum, entry) => sum + (entry.placesCountContract ?? 0), 0)
    : (pool?.placesCountContract ?? null)

  const SLICE_GAP_PCT = donutRows.length > 1 ? 0.5 : 0

  const donutParts = donutRows.reduce<{ stops: string[]; cumPct: number }>(
    (acc, row, i) => {
      const pct =
        latestTotals.capacity > 0 ? (row.placesNow / latestTotals.capacity) * 100 : 0
      const gap = pct > SLICE_GAP_PCT * 3 ? SLICE_GAP_PCT : 0
      const color = seriesColor(i, row.isOther)
      const end = acc.cumPct + pct
      acc.stops.push(`${color} ${acc.cumPct}% ${end - gap}%`)
      if (gap > 0) acc.stops.push(`${colors.surface} ${end - gap}% ${end}%`)
      acc.cumPct = end
      return acc
    },
    { stops: [], cumPct: 0 },
  )
  const donutGradient =
    donutParts.cumPct > 0
      ? `conic-gradient(${donutParts.stops.join(',')})`
      : `conic-gradient(${colors.border} 0% 100%)`

  if (loading && !data) return <FullScreenLoader />

  if (!pool) {
    return (
      <EmptyState
        icon={<BarChartOutlinedIcon sx={{ fontSize: 34, color: colors.slate }} />}
        title={t('no_parking_data')}
        description={t('no_parking_data_desc')}
      />
    )
  }

  const configItems = [
    { label: t('cfg_parkings'), value: poolParkings.length > 0 ? poolParkings.length : NONE, color: colors.navy },
    { label: t('cfg_places_count'), value: contractPlaces ?? NONE, color: colors.gold },
    {
      label: t('cfg_max_users'),
      value: pool.maxActiveUserCountUnlimited
        ? t('cfg_unlimited')
        : pool.maxActiveUserCount ?? '—',
      color: colors.textBody,
    },
    { label: t('cfg_profiles'), value: profiles.length > 0 ? profiles.length : NONE, color: colors.textBody },
  ]

  const deltaLabel =
    deltaPp == null
      ? null
      : deltaPp === 0
        ? t('kpi_delta_flat')
        : t(deltaPp > 0 ? 'kpi_delta_up' : 'kpi_delta_down', { pp: Math.abs(deltaPp) })

  const snapshotDay = isSingleDay ? day : latestDay
  const today = dayjs().format('YYYY-MM-DD')

  const isFutureDay = !!snapshotDay && snapshotDay > today

  const snapshotLabel = !snapshotDay
    ? t('buildings_as_of_today')
    : isFutureDay
      ? t('buildings_planned_for', {
          date: dateIso2localeString(snapshotDay, i18n.language, DATE_FORMAT),
        })
      : snapshotDay === today
        ? t('buildings_as_of_today')
        : t('buildings_as_of', {
            date: dateIso2localeString(snapshotDay, i18n.language, DATE_FORMAT),
          })

  const periodLabel = isSingleDay
    ? dateIso2localeString(day, i18n.language, DATE_FORMAT)
    : t(PERIOD_LABEL_KEYS[period])

  const statsNotice = statsFailed
    ? t('stats_error')
    : !statsPending && stats.length === 0
      ? t('no_stats')
      : null

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: colors.radiusHero,
          p: '30px 34px',
          mb: '18px',
          background: 'linear-gradient(120deg,#1A4A66 0%,#0E2C3B 58%)',
          boxShadow: colors.shadowHero,
        }}
      >
        <Box sx={{ position: 'absolute', top: '-70px', left: '-30px', width: '280px', height: '280px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(237,189,1,0.20), transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: '-90px', right: '60px', width: '300px', height: '300px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(79,143,82,0.22), transparent 70%)', pointerEvents: 'none' }} />

        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '24px', flexWrap: 'wrap' }}>
          <Box>
            <Box sx={{ fontSize: '12px', letterSpacing: '0.14em', textTransform: 'uppercase', color: colors.greenMint, fontWeight: 800 }}>
              {t('eyebrow')}
            </Box>
            <Typography component="h1" sx={{ fontSize: '34px', fontWeight: 800, letterSpacing: '-0.02em', mt: '9px', mb: '7px', color: '#fff' }}>
              {t('title')}
            </Typography>
            <Typography sx={{ fontSize: '14.5px', color: 'rgba(255,255,255,0.62)', m: 0, maxWidth: '520px' }}>
              {t('subtitle')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '13px' }}>
            {SHOW_UTILIZATION_STATS && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)', p: '9px 16px', borderRadius: '30px' }}>
                <Box component="span" sx={{ fontSize: '21px', fontWeight: 800, color: colors.greenMint }}>
                  {utilization != null ? `${utilization}%` : NONE}
                </Box>
                <Box component="span" sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.78)', fontWeight: 600 }}>
                  {t('avg_utilization_short')}
                </Box>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {isSingleDay && <DayPickerPill value={day} onChange={setDay} />}

              <Box sx={{ display: 'flex', gap: '4px', background: 'rgba(255,255,255,0.08)', p: '4px', borderRadius: '24px' }}>
              {PERIODS.map(p => (
                <Box
                  key={p}
                  onClick={() => setPeriod(p)}
                  sx={{
                    p: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '13px',
                    fontWeight: period === p ? 700 : 600,
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'background .15s, color .15s',
                    background: period === p ? '#fff' : 'transparent',
                    color: period === p ? colors.navy : 'rgba(255,255,255,0.72)',
                  }}
                >
                  {t(PERIOD_LABEL_KEYS[p])}
                </Box>
              ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          background: colors.surface,
          border: `1px solid ${colors.border2}`,
          borderRadius: colors.radiusCard,
          boxShadow: colors.shadowCard,
          p: '16px 20px',
          mb: '16px',
        }}
      >
        <Box sx={{ fontSize: '12px', letterSpacing: '0.04em', textTransform: 'uppercase', color: colors.slate, fontWeight: 700, mb: '12px' }}>
          {t('product_config')}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${configItems.length}, 1fr)`, gap: '14px' }}>
          {configItems.map((item, i) => (
            <Box key={item.label} sx={i > 0 ? { borderLeft: `1px solid ${colors.border}`, pl: '14px' } : {}}>
              <Box sx={{ fontSize: '12px', color: colors.textMuted, fontWeight: 600 }}>{item.label}</Box>
              <Box sx={{ fontSize: '23px', fontWeight: 800, mt: '3px', color: item.color }}>
                {item.value}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {SHOW_PROFILES && (
        <Box
          sx={{
            background: colors.surface,
            border: `1px solid ${colors.border2}`,
            borderRadius: colors.radiusCard,
            boxShadow: colors.shadowCard,
            p: '16px 20px',
            mb: '16px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', mb: '12px' }}>
            <Box sx={{ fontSize: '12px', letterSpacing: '0.04em', textTransform: 'uppercase', color: colors.slate, fontWeight: 700 }}>
              {t('profiles_title')}
            </Box>
            <Box sx={{ fontSize: '12px', color: colors.textMuted, fontWeight: 600 }}>
              {t('profiles_hint')}
            </Box>
          </Box>
          {profiles.length > 0 ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' }}>
              {profiles.map(profile => (
                <Box
                  key={profile.id}
                  sx={{
                    border: `1px solid ${colors.border}`,
                    borderRadius: '14px',
                    p: '14px 16px',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <Box sx={{ fontSize: '14px', fontWeight: 800, color: colors.navy }}>{profile.name}</Box>
                    {profile.usersAggregate?.count != null && (
                      <Box
                        component="span"
                        sx={{
                          background: colors.rowSelected,
                          color: colors.navy,
                          fontSize: '11px',
                          fontWeight: 800,
                          p: '3px 8px',
                          borderRadius: colors.radiusTag,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t('profile_users', { n: profile.usersAggregate.count })}
                      </Box>
                    )}
                  </Box>
                  <ProfileLimitGrid profile={profile} />
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ fontSize: '13px', color: colors.textMuted, fontWeight: 600 }}>{t('no_profiles')}</Box>
          )}
        </Box>
      )}

      {statsNotice && (
        <Box
          sx={{
            background: statsFailed ? colors.redBg : colors.surface,
            border: `1px solid ${statsFailed ? colors.redBorder : colors.border2}`,
            borderRadius: colors.radiusCard,
            p: '14px 20px',
            mb: '16px',
            fontSize: '13px',
            fontWeight: 700,
            color: statsFailed ? colors.redDark : colors.textMuted,
          }}
        >
          {statsNotice}
        </Box>
      )}

      {SHOW_UTILIZATION_STATS && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: '16px', mb: '16px' }}>
          <Box sx={{ background: 'linear-gradient(135deg,#6FB877,#4F8F52)', borderRadius: colors.radiusCard, p: '18px', boxShadow: colors.shadowKpiGreen }}>
            <Box sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.88)', fontWeight: 700 }}>{t('kpi_avg_utilization')}</Box>
            <Box sx={{ fontSize: '28px', fontWeight: 800, mt: '4px', color: '#fff' }}>
              <StatValue
                loading={statsPending}
                error={statsFailed}
                errorMessage={t('stats_error')}
                empty={utilization == null}
                onDark
                height={34}
              >
                {utilization}
                <Box component="span" sx={{ fontSize: '17px' }}>%</Box>
              </StatValue>
            </Box>
            <Box sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.9)', fontWeight: 700, mt: '2px' }}>
              {deltaLabel ?? t('kpi_period_hint')}
            </Box>
          </Box>
        </Box>
      )}

      <BuildingsTable
        rows={buildingRows}
        pending={statsPending}
        failed={statsFailed}
        showUtilization={SHOW_UTILIZATION_STATS}
        snapshotLabel={snapshotLabel}
        futureDay={isFutureDay}
      />

      {SHOW_EMPLOYEE_USAGE && (
        <EmployeeUsageSection
          stats={employeeRows}
          pending={employeePending}
          failed={employeeFailed}
        />
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: SHOW_UTILIZATION_STATS ? '1.35fr 1fr' : 'minmax(0, 620px)', gap: '16px' }}>
        {SHOW_UTILIZATION_STATS && (
          <Box sx={{ background: colors.surface, border: `1px solid ${colors.border2}`, borderRadius: colors.radiusCard, boxShadow: colors.shadowCard, p: '20px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '14px' }}>
              <Box sx={{ fontSize: '15px', fontWeight: 800 }}>{t('chart_usage_over_time')}</Box>
              <Box sx={{ fontSize: '12px', color: colors.textMuted, fontWeight: 600 }}>
                {periodLabel}
              </Box>
            </Box>
            {dayTotals.length > 1 ? (
              <UsageOverTimeChart days={dayTotals} />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  borderRadius: '12px',
                  background: colors.bg,
                  color: colors.textMuted,
                  fontSize: '14px',
                  fontWeight: 700,
                  textAlign: 'center',
                  p: '0 20px',
                }}
              >
                {statsPending ? t('loading_stats') : t('no_stats')}
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ background: colors.surface, border: `1px solid ${colors.border2}`, borderRadius: colors.radiusCard, boxShadow: colors.shadowCard, p: '20px' }}>
          <Box sx={{ fontSize: '15px', fontWeight: 800, mb: '14px' }}>{t('chart_parkings_title')}</Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
            <Box sx={{ width: '128px', height: '128px', borderRadius: '50%', flexShrink: 0, background: donutGradient, position: 'relative' }}>
              <Box sx={{ position: 'absolute', inset: '23px', background: colors.surface, borderRadius: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ fontSize: '22px', fontWeight: 800, lineHeight: 1 }}>
                  <StatValue
                    loading={statsPending}
                    error={statsFailed && contractPlaces == null}
                    errorMessage={t('stats_error')}
                    empty={latestTotals.capacity === 0 && contractPlaces == null}
                    width={52}
                    height={24}
                  >
                    {latestTotals.capacity > 0 ? latestTotals.capacity : contractPlaces}
                  </StatValue>
                </Box>
                <Box sx={{ fontSize: '10.5px', color: colors.slate, fontWeight: 600 }}>{t('places')}</Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '12.5px', flex: 1, minWidth: 0 }}>
              {donutRows.length > 0 ? (
                donutRows.map((row, i) => (
                  <Box key={row.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Box component="span" sx={{ width: '11px', height: '11px', borderRadius: '3px', background: seriesColor(i, row.isOther), flexShrink: 0 }} />
                      <Box component="span" sx={{ flex: 1, minWidth: 0, color: colors.textBody, fontWeight: 600 }}>{row.name}</Box>
                      <Box component="span" sx={{ color: colors.navy, fontWeight: 800, whiteSpace: 'nowrap' }}>
                        {t('places_count', { n: row.placesNow })}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '3px', pl: '19px' }}>
                      <Box sx={{ flex: 1, height: 5, borderRadius: '3px', background: colors.border, overflow: 'hidden' }}>
                        <Box sx={{ width: `${isFutureDay ? 0 : Math.min(row.pct ?? 0, 100)}%`, height: '100%', background: seriesColor(i, row.isOther) }} />
                      </Box>
                      <Box component="span" sx={{ color: colors.textMuted, fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {isFutureDay
                          ? t('utilization_unknown_yet')
                          : row.pct != null
                            ? t('parking_utilization', { pct: row.pct })
                            : NONE}
                      </Box>
                    </Box>
                  </Box>
                ))
              ) : (
                <StatValue
                  loading={statsPending}
                  error={statsFailed}
                  errorMessage={t('stats_error')}
                  empty={!statsPending && !statsFailed}
                  width={140}
                  height={18}
                >
                  {null}
                </StatValue>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: '14px', pt: '14px', borderTop: `1px solid ${colors.border}` }}>
            <Box sx={{ fontSize: '12.5px', color: colors.textMuted, fontWeight: 600 }}>
              {latestIsToday
                ? t('total_available_today')
                : t('total_available_on', {
                    date: latestDay
                      ? dayjs(latestDay).format('DD.MM')
                      : NONE,
                  })}
            </Box>
            <Box sx={{ fontSize: '13px', fontWeight: 800, color: colors.green }}>
              <StatValue
                loading={statsPending}
                error={statsFailed}
                errorMessage={t('stats_error')}
                empty={latestRows.length === 0}
                width={62}
                height={18}
              >
                {latestTotals.free} / {latestTotals.capacity}
              </StatValue>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default ParkingPoolData
